import { Alert, Button, Card, Form, Input, message } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type FormValues = { committee_code: string; name: string; purpose?: string };

export default function GovernanceCommitteeFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.governance.manage') || user?.permissions?.includes('education.admin')
  );

  const create = useMutation({
    mutationFn: (values: FormValues) => api.post<{ id: string }>('/education/governance/committees', values),
    onSuccess: (res) => {
      message.success(t('committeeCreated'));
      const id = res.data?.id;
      navigate(id ? `/education/governance/committees/${id}` : '/education/governance/committees');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={t('newCommittee')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('newCommittee')}
      description={t('governanceCommitteesIntro')}
      extra={
        <Button onClick={() => navigate('/education/governance/committees')}>{t('governanceCommittees')}</Button>
      }
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Form layout="vertical" onFinish={(values) => create.mutate(values)}>
          <Form.Item name="committee_code" label={t('committeeCode')} rules={[{ required: true }]}>
            <Input maxLength={40} placeholder="SMC" />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="purpose" label={t('purpose')}>
            <Input.TextArea rows={3} maxLength={1000} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={create.isPending}>
            {t('save')}
          </Button>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
